import { useCallback, useState } from "react";
import { useProjects } from "@contexts/ProjectContext";
import type { Project } from "@shared/types";
import { buildProjectContext, hashContext } from "@domain/project/ProjectContext";
import { callProjectReviewer, type AIReview } from "@data/functions/projectReviewer";
import {
  budgetRepository,
  phaseRepository,
  contractorRepository,
  floorplanRepository,
  designItemRepository,
  feedbackRepository,
  permitsChecklistRepository,
} from "@data/firestore";
import { useRejectedObservations } from "@features/chat/hooks/useRejectedObservations";
// Per-project sqft/spaces live in domain for now (Phase 2+ will store per-project).
import { planBSqFt, planBTotalSqFt, planBSpaces } from "@domain/budget/planB";
import { projectData } from "@domain/project/projectData";

interface ProjectContextValue {
  activeProject: Project | null | undefined;
}

export interface UseProjectReviewResult {
  review: AIReview | null;
  loading: boolean;
  error: string | null;
  run: () => Promise<void>;
  forceRefresh: () => Promise<void>;
  clear: () => void;
}

/**
 * Gathers the current project state on-demand, builds the LLM context,
 * calls projectReviewer, and surfaces the result. Pulls everything from
 * repositories so the Dashboard doesn't have to pre-subscribe to data it
 * only needs when the user clicks "Review".
 *
 * Uses hardcoded sqft/spaces from planB for now — Phase 2 will move those
 * to per-project configuration.
 */
export function useProjectReview(): UseProjectReviewResult {
  const projects = useProjects() as ProjectContextValue;
  const activeProject = projects.activeProject;
  const { items: rejectedItems } = useRejectedObservations();
  const [review, setReview] = useState<AIReview | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const invoke = useCallback(
    async (forceRefresh: boolean) => {
      if (!activeProject) {
        setError("No active project");
        return;
      }
      const ownerUid = activeProject.ownerUid;
      const projectId = activeProject.id;
      setLoading(true);
      setError(null);
      try {
        const [
          budget,
          phases,
          contractors,
          floorplans,
          designItems,
          feedbackTickets,
          permitsChecklist,
        ] = await Promise.all([
          budgetRepository.get(activeProject),
          phaseRepository.get(activeProject),
          contractorRepository.get(activeProject),
          floorplanRepository.get(activeProject),
          designItemRepository.get(activeProject),
          feedbackRepository.get(activeProject),
          permitsChecklistRepository.get(activeProject),
        ]);

        const context = buildProjectContext({
          project: activeProject,
          budget,
          phases,
          contractors: contractors as unknown as Array<Record<string, unknown>>,
          floorplans: floorplans.map((f) => ({
            name: f.name,
            ext: f.ext,
            size: f.size,
          })),
          designItems: designItems as unknown as Array<Record<string, unknown>>,
          feedbackTickets: feedbackTickets.map((t) => ({
            status: (t as { status?: string }).status,
          })),
          permitsChecklist,
          sqFt: {
            current: projectData.currentSqFt,
            expansion: planBSqFt,
            total: planBTotalSqFt,
          },
          spaces: planBSpaces,
        });
        const rejectedTitles = rejectedItems.map((r) => r.title);
        // Rejections must invalidate the cache — otherwise a stale review
        // containing the just-rejected observation would be served.
        const contextHash = hashContext(context, rejectedTitles);

        const result = await callProjectReviewer({
          ownerUid,
          projectId,
          contextHash,
          forceRefresh,
          context,
          rejectedTitles,
        });
        setReview(result);
      } catch (err) {
        console.error("[useProjectReview]", err);
        const message =
          (err as { message?: string })?.message ?? String(err);
        setError(message);
      } finally {
        setLoading(false);
      }
    },
    [activeProject, rejectedItems]
  );

  const run = useCallback(() => invoke(false), [invoke]);
  const forceRefresh = useCallback(() => invoke(true), [invoke]);
  const clear = useCallback(() => {
    setReview(null);
    setError(null);
  }, []);

  return { review, loading, error, run, forceRefresh, clear };
}
